import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input, Label, Select } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import SmartModal from '../../ui/SmartModal';
import { toast } from 'sonner';
import { 
  Globe, 
  Plus, 
  TestTube, 
  Key, 
  Webhook, 
  Settings, 
  Trash2, 
  Copy, 
  RefreshCw,
  MessageSquare,
  Database,
  Video,
  CreditCard,
  Phone,
  Layout,
  Users,
  Info,
  CloudUpload,
  CheckCircle2,
  XCircle,
  Clock,
  CalendarDays
} from 'lucide-react';
import { http as api } from '../../../lib/api';

const Integraciones = () => {
  const [integraciones, setIntegraciones] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('integraciones');
  
  // Estados para formularios
  const [mostrarFormIntegracion, setMostrarFormIntegracion] = useState(false);
  const [mostrarFormWebhook, setMostrarFormWebhook] = useState(false);
  const [mostrarFormApiKey, setMostrarFormApiKey] = useState(false);
  
  // Backup GCS
  const [backupHistorial, setBackupHistorial] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);

  const cargarHistorialBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get('/admin/integraciones/drive/backup/historial?limit=20');
      setBackupHistorial(res.data || []);
    } catch (e) {
      console.error('Error cargando historial backup:', e);
    } finally {
      setBackupLoading(false);
    }
  };

  const ejecutarBackup = async () => {
    setBackupRunning(true);
    try {
      const res = await api.post('/admin/integraciones/drive/backup', {});
      const d = res.data;
      if (d.success) {
        toast.success(`Backup completado: ${d.size_mb} MB subidos a GCS (${d.duration_s}s)`);
      } else {
        toast.error(d.message || 'Backup fallido');
      }
      await cargarHistorialBackup();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error ejecutando backup');
    } finally {
      setBackupRunning(false);
    }
  };

  const [nuevaIntegracion, setNuevaIntegracion] = useState({
    nombre: '',
    tipo: '',
    configuracion: { api_key: '', webhook_url: '', client_id: '', client_secret: '', additional_settings: {} },
    activa: true
  });
  
  const [nuevoWebhook, setNuevoWebhook] = useState({
    nombre: '',
    url: '',
    eventos: [],
    metodo: 'POST',
    headers: {},
    activo: true
  });
  
  const [nuevaApiKey, setNuevaApiKey] = useState({
    nombre: '',
    permisos: [],
    expiracion: ''
  });

  const tiposIntegracion = [
    // Comunicación & Notificaciones
    { value: 'whatsapp', label: 'WhatsApp Business', icon: <Phone className="h-6 w-6 text-green-500" />, category: 'Comunicación', descripcion: 'Envía notificaciones automáticas a clientes por WhatsApp.' },
    { value: 'slack', label: 'Slack', icon: <MessageSquare className="h-6 w-6 text-purple-500" />, category: 'Comunicación', descripcion: 'Recibe alertas del sistema en tus canales de Slack.' },
    { value: 'teams', label: 'Microsoft Teams', icon: <Users className="h-6 w-6 text-emerald-600" />, category: 'Comunicación', descripcion: 'Integra notificaciones y alertas en MS Teams.' },
    { value: 'telegram', label: 'Telegram', icon: <MessageSquare className="h-6 w-6 text-emerald-400" />, category: 'Comunicación', descripcion: 'Bot de notificaciones para grupos o usuarios de Telegram.' },
    { value: 'discord', label: 'Discord', icon: <MessageSquare className="h-6 w-6 text-teal-500" />, category: 'Comunicación', descripcion: 'Webhooks para canales de Discord.' },
    { value: 'twilio', label: 'Twilio SMS', icon: <Phone className="h-6 w-6 text-red-500" />, category: 'Comunicación', descripcion: 'Envío de SMS transaccionales y alertas.' },
    
    // Productividad & Calendario
    { value: 'calendar', label: 'Google Calendar', icon: <Layout className="h-6 w-6 text-emerald-500" />, category: 'Productividad', descripcion: 'Sincroniza agendamientos con Google Calendar.' },
    { value: 'zoom', label: 'Zoom', icon: <Video className="h-6 w-6 text-emerald-500" />, category: 'Productividad', descripcion: 'Genera enlaces de reuniones automáticamente.' },
    { value: 'trello', label: 'Trello', icon: <Layout className="h-6 w-6 text-emerald-400" />, category: 'Gestión', descripcion: 'Crea tarjetas en Trello al generar órdenes.' },
    { value: 'asana', label: 'Asana', icon: <Layout className="h-6 w-6 text-red-400" />, category: 'Gestión', descripcion: 'Sincroniza tareas de mantenimiento con Asana.' },
    { value: 'jira', label: 'Jira', icon: <Layout className="h-6 w-6 text-emerald-600" />, category: 'Gestión', descripcion: 'Gestión de tickets avanzada con Jira.' },

    // Almacenamiento
    { value: 'drive', label: 'Google Drive', icon: <Database className="h-6 w-6 text-green-500" />, category: 'Almacenamiento', descripcion: 'Respalda documentos y reportes en Drive.' },
    { value: 'dropbox', label: 'Dropbox', icon: <Database className="h-6 w-6 text-emerald-500" />, category: 'Almacenamiento', descripcion: 'Almacenamiento en la nube para backups.' },

    // Pagos & CRM
    { value: 'stripe', label: 'Stripe', icon: <CreditCard className="h-6 w-6 text-teal-600" />, category: 'Pagos', descripcion: 'Procesa pagos de servicios online.' },
    { value: 'paypal', label: 'PayPal', icon: <CreditCard className="h-6 w-6 text-emerald-700" />, category: 'Pagos', descripcion: 'Pagos internacionales y locales.' },
    { value: 'hubspot', label: 'HubSpot', icon: <Users className="h-6 w-6 text-orange-500" />, category: 'CRM', descripcion: 'Sincroniza clientes con HubSpot CRM.' },
    { value: 'salesforce', label: 'Salesforce', icon: <Users className="h-6 w-6 text-emerald-400" />, category: 'CRM', descripcion: 'Integración empresarial con Salesforce.' },
    { value: 'zapier', label: 'Zapier', icon: <Globe className="h-6 w-6 text-orange-500" />, category: 'Automatización', descripcion: 'Conecta con más de 3000 apps vía Zapier.' },
  ];

  const eventosWebhook = [
    'orden.creada',
    'orden.actualizada',
    'orden.completada',
    'cliente.creado',
    'equipo.actualizado',
    'backup.completado',
    'usuario.login',
    'sistema.error'
  ];

  const permisosApi = [
    'ordenes:read',
    'ordenes:write',
    'clientes:read',
    'clientes:write',
    'equipos:read',
    'equipos:write',
    'usuarios:read',
    'reportes:read',
    'admin:all'
  ];

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [integRes, webhookRes, apiRes] = await Promise.all([
        api.get('/admin/integraciones'),
        api.get('/admin/webhooks'),
        api.get('/admin/api-keys')
      ]);
      
      setIntegraciones(integRes.data || []);
      setWebhooks(webhookRes.data || []);
      setApiKeys(apiRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar las integraciones');
    } finally {
      setLoading(false);
    }
  };

  const probarIntegracion = async (id) => {
    try {
      const res = await api.post(`/admin/integraciones/${id}/probar`);
      toast.success(res.data?.message || 'Conexión probada exitosamente');
    } catch (error) {
      console.error('Error al probar integración:', error);
      const msg = error.response?.data?.detail || 'Error al probar la conexión';
      toast.error(msg);
    }
  };

  const eliminarIntegracion = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta integración? Se perderá la configuración.')) return;
    try {
      await api.delete(`/admin/integraciones/${id}`);
      toast.success('Integración eliminada');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar la integración');
    }
  };

  const probarWebhook = async (id) => {
    try {
      await api.post(`/admin/webhooks/${id}/probar`);
      toast.success('Webhook de prueba enviado');
    } catch (error) {
      console.error('Error al probar webhook:', error);
      toast.error('Error al probar el webhook');
    }
  };

  const eliminarWebhook = async (id) => {
    if (!window.confirm('¿Eliminar este webhook?')) return;
    try {
      await api.delete(`/admin/webhooks/${id}`);
      toast.success('Webhook eliminado');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar el webhook');
    }
  };

  const copiarApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('Copiado al portapapeles');
  };

  const regenerarApiKey = async (id) => {
    if (!window.confirm('¿Regenerar Key? La anterior dejará de funcionar inmediatamente.')) return;
    try {
      const response = await api.post(`/admin/api-keys/${id}/regenerar`);
      setApiKeys(prev => prev.map(key => 
        key.id === id ? { ...key, key: response.data.key } : key
      ));
      toast.success('Key regenerada exitosamente');
    } catch (error) {
      console.error('Error al regenerar:', error);
      toast.error('Error al regenerar la Key');
    }
  };

  const eliminarApiKey = async (id) => {
    if (!window.confirm('¿Eliminar esta API Key permanentemente?')) return;
    try {
      await api.delete(`/admin/api-keys/${id}`);
      toast.success('API Key eliminada');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar la API Key');
    }
  };

  const guardarIntegracion = async () => {
    if (!nuevaIntegracion.nombre || !nuevaIntegracion.tipo) {
      toast.error('Nombre y Tipo son obligatorios');
      return;
    }
    try {
      await api.post('/admin/integraciones', nuevaIntegracion);
      toast.success('Integración guardada correctamente');
      setMostrarFormIntegracion(false);
      setNuevaIntegracion({ nombre: '', tipo: '', configuracion: { api_key: '', webhook_url: '', client_id: '', client_secret: '', additional_settings: {} }, activa: true });
      cargarDatos();
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar la integración');
    }
  };

  const guardarWebhook = async () => {
    if (!nuevoWebhook.nombre || !nuevoWebhook.url) {
      toast.error('Nombre y URL son obligatorios');
      return;
    }
    try {
      await api.post('/admin/webhooks', nuevoWebhook);
      toast.success('Webhook configurado');
      setMostrarFormWebhook(false);
      setNuevoWebhook({ nombre: '', url: '', eventos: [], metodo: 'POST', headers: {}, activo: true });
      cargarDatos();
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al configurar el webhook');
    }
  };

  const guardarApiKey = async () => {
    if (!nuevaApiKey.nombre) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      const response = await api.post('/admin/api-keys', nuevaApiKey);
      toast.success('API Key generada');
      setMostrarFormApiKey(false);
      setNuevaApiKey({ nombre: '', permisos: [], expiracion: '' });
      cargarDatos();
      if (response.data.key) {
        alert(`TU NUEVA API KEY:\n\n${response.data.key}\n\nCópiala ahora. No podrás verla nuevamente.`);
      }
    } catch (error) {
      console.error('Error al crear:', error);
      toast.error('Error al generar la API Key');
    }
  };

  const toggleEventoWebhook = (evento) => {
    setNuevoWebhook(prev => {
      const eventos = prev.eventos.includes(evento)
        ? prev.eventos.filter(e => e !== evento)
        : [...prev.eventos, evento];
      return { ...prev, eventos };
    });
  };

  const togglePermisoApiKey = (permiso) => {
    setNuevaApiKey(prev => {
      const permisos = prev.permisos.includes(permiso)
        ? prev.permisos.filter(p => p !== permiso)
        : [...prev.permisos, permiso];
      return { ...prev, permisos };
    });
  };

  // Renderizado de campos dinámicos según el tipo de integración
  const renderCamposConfiguracion = () => {
    const tipo = tiposIntegracion.find(t => t.value === nuevaIntegracion.tipo);
    if (!tipo) return null;

    return (
      <div className="space-y-3 border-t pt-3 mt-3">
        <div className="bg-emerald-50 p-3 rounded-md flex items-start space-x-2 text-sm text-emerald-800">
          <Info className="h-5 w-5 shrink-0 mt-0.5" />
          <p>{tipo.descripcion}</p>
        </div>

        {['slack', 'discord', 'teams'].includes(nuevaIntegracion.tipo) && (
          <div>
            <Label>Webhook URL</Label>
            <Input 
              value={nuevaIntegracion.configuracion.webhook_url || ''} 
              onChange={e => setNuevaIntegracion({...nuevaIntegracion, configuracion: {...nuevaIntegracion.configuracion, webhook_url: e.target.value}})} 
              placeholder={`https://hooks.${nuevaIntegracion.tipo}.com/...`}
            />
            <p className="text-xs text-gray-500 mt-1">Pega aquí la URL del webhook proporcionada por {tipo.label}.</p>
          </div>
        )}

        {nuevaIntegracion.tipo === 'telegram' && (
          <div className="space-y-3">
            <div>
              <Label>Bot Token <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={nuevaIntegracion.configuracion.additional_settings?.bot_token || ''}
                onChange={e => setNuevaIntegracion({
                  ...nuevaIntegracion,
                  configuracion: {
                    ...nuevaIntegracion.configuracion,
                    additional_settings: {
                      ...nuevaIntegracion.configuracion.additional_settings,
                      bot_token: e.target.value
                    }
                  }
                })}
                placeholder="123456789:AAFxxxxxxxxxxxxxxxxxxxxxx"
              />
              <p className="text-xs text-gray-500 mt-1">Obtén el token hablando con <b>@BotFather</b> en Telegram → /newbot.</p>
            </div>
            <div>
              <Label>Chat ID <span className="text-red-500">*</span></Label>
              <Input
                value={nuevaIntegracion.configuracion.additional_settings?.chat_id || ''}
                onChange={e => setNuevaIntegracion({
                  ...nuevaIntegracion,
                  configuracion: {
                    ...nuevaIntegracion.configuracion,
                    additional_settings: {
                      ...nuevaIntegracion.configuracion.additional_settings,
                      chat_id: e.target.value
                    }
                  }
                })}
                placeholder="-1001234567890"
              />
              <p className="text-xs text-gray-500 mt-1">ID del grupo o usuario. Usa <b>@userinfobot</b> para obtenerlo. Los grupos empiezan con -100.</p>
            </div>
          </div>
        )}

        {['whatsapp', 'twilio', 'stripe', 'paypal', 'hubspot', 'salesforce'].includes(nuevaIntegracion.tipo) && (
          <>
            <div>
              <Label>API Key / Token</Label>
              <Input 
                type="password"
                value={nuevaIntegracion.configuracion.api_key || ''} 
                onChange={e => setNuevaIntegracion({...nuevaIntegracion, configuracion: {...nuevaIntegracion.configuracion, api_key: e.target.value}})} 
                placeholder="sk_live_..."
              />
            </div>
            {['stripe', 'paypal'].includes(nuevaIntegracion.tipo) && (
               <div>
                 <Label>Client Secret (Opcional)</Label>
                 <Input 
                   type="password"
                   value={nuevaIntegracion.configuracion.client_secret || ''} 
                   onChange={e => setNuevaIntegracion({...nuevaIntegracion, configuracion: {...nuevaIntegracion.configuracion, client_secret: e.target.value}})} 
                 />
               </div>
            )}
          </>
        )}

        {nuevaIntegracion.tipo === 'drive' && (
          <div className="space-y-3">
            <div>
              <Label>Service Account JSON <span className="text-red-500">*</span></Label>
              <textarea
                rows={6}
                className="w-full mt-1 text-xs font-mono border rounded-md p-2 resize-y bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                placeholder={'{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'}
                value={nuevaIntegracion.configuracion.additional_settings?.service_account_json || ''}
                onChange={e => setNuevaIntegracion({
                  ...nuevaIntegracion,
                  configuracion: {
                    ...nuevaIntegracion.configuracion,
                    additional_settings: {
                      ...nuevaIntegracion.configuracion.additional_settings,
                      service_account_json: e.target.value
                    }
                  }
                })}
              />
              <p className="text-xs text-gray-500 mt-1">Pega el contenido completo del archivo JSON de la cuenta de servicio de Google.</p>
            </div>
            <div>
              <Label>Nombre del Bucket (Google Cloud Storage) <span className="text-red-500">*</span></Label>
              <Input
                value={nuevaIntegracion.configuracion.additional_settings?.bucket_name || ''}
                onChange={e => setNuevaIntegracion({
                  ...nuevaIntegracion,
                  configuracion: {
                    ...nuevaIntegracion.configuracion,
                    additional_settings: {
                      ...nuevaIntegracion.configuracion.additional_settings,
                      bucket_name: e.target.value
                    }
                  }
                })}
                placeholder="mi-bucket-elementos"
              />
              <p className="text-xs text-gray-500 mt-1">
                Crea un bucket en <b>Google Cloud Console → Cloud Storage</b> y otorga al service account el rol <b>Storage Object Admin</b>. Pega aquí el nombre del bucket.
              </p>
            </div>
          </div>
        )}

        {['calendar', 'zoom'].includes(nuevaIntegracion.tipo) && (
          <div className="text-center py-4">
            <Button variant="outline" className="w-full">
              <Globe className="mr-2 h-4 w-4" />
              Conectar cuenta de {tipo.label}
            </Button>
            <p className="text-xs text-gray-500 mt-2">Se abrirá una ventana para autorizar el acceso.</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'backup') cargarHistorialBackup(); }}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="integraciones">Integraciones</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="api-keys">API Keys</TabsTrigger>
          <TabsTrigger value="backup">Backup GCS</TabsTrigger>
        </TabsList>

        {/* Tab Integraciones */}
        <TabsContent value="integraciones" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Catálogo de Integraciones</h3>
              <p className="text-sm text-muted-foreground">
                Amplía las capacidades del sistema conectando tus herramientas favoritas.
              </p>
            </div>
            <Button onClick={() => {
              setNuevaIntegracion({ nombre: '', tipo: '', configuracion: {}, activa: true });
              setMostrarFormIntegracion(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva Integración
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiposIntegracion.map((tipo) => {
              const integExistente = integraciones.find(i => i.tipo === tipo.value);
              return (
                <Card key={tipo.value} className={`cursor-pointer transition-all hover:shadow-md ${integExistente ? 'border-emerald-200 bg-emerald-50/30' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white rounded-lg shadow-sm border">
                          {tipo.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-sm">{tipo.label}</h4>
                          <span className="text-xs text-gray-500">{tipo.category}</span>
                        </div>
                      </div>
                      {integExistente ? (
                        <Badge className={integExistente.activa ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {integExistente.activa ? 'Conectado' : 'Pausado'}
                        </Badge>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => {
                          setNuevaIntegracion({...nuevaIntegracion, tipo: tipo.value, nombre: tipo.label});
                          setMostrarFormIntegracion(true);
                        }}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <p className="text-xs text-gray-600 mb-4 line-clamp-2 min-h-[2.5em]">
                      {tipo.descripcion}
                    </p>
                    
                    {integExistente ? (
                      <div className="flex justify-between items-center border-t pt-3">
                        <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => probarIntegracion(integExistente.id)}>
                          <TestTube className="h-3 w-3 mr-1" /> Probar
                        </Button>
                        <div className="flex space-x-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
                             setNuevaIntegracion({...integExistente});
                             setMostrarFormIntegracion(true);
                          }}>
                            <Settings className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => eliminarIntegracion(integExistente.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => {
                        setNuevaIntegracion({...nuevaIntegracion, tipo: tipo.value, nombre: tipo.label});
                        setMostrarFormIntegracion(true);
                      }}>
                        Configurar
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tab Webhooks */}
        <TabsContent value="webhooks" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Webhooks Salientes</h3>
              <p className="text-sm text-muted-foreground">
                Notifica a sistemas externos cuando ocurren eventos en Elements.
              </p>
            </div>
            <Button onClick={() => setMostrarFormWebhook(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Webhook
            </Button>
          </div>

          {webhooks.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed">
              <Webhook className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-gray-900 font-medium">No hay webhooks configurados</h3>
              <p className="text-gray-500 text-sm mt-1">Crea uno para empezar a recibir eventos.</p>
            </div>
          )}

          <div className="grid gap-4">
            {webhooks.map((webhook) => (
              <Card key={webhook.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <Webhook className="h-4 w-4 text-purple-600" />
                        <h4 className="font-medium">{webhook.nombre}</h4>
                        <Badge className={webhook.activo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {webhook.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 block mt-1 mb-2 w-fit">
                        {webhook.url}
                      </code>
                      <div className="flex flex-wrap gap-1">
                        {webhook.eventos.map(evt => (
                          <span key={evt} className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-100">
                            {evt}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => probarWebhook(webhook.id)}>
                        <TestTube className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => eliminarWebhook(webhook.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab API Keys */}
        <TabsContent value="api-keys" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Credenciales de API</h3>
              <p className="text-sm text-muted-foreground">
                Genera claves para permitir que aplicaciones externas accedan a Elements.
              </p>
            </div>
            <Button onClick={() => setMostrarFormApiKey(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva API Key
            </Button>
          </div>

          <div className="grid gap-4">
            {apiKeys.map((apiKey) => (
              <Card key={apiKey.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Key className="h-4 w-4 text-amber-500" />
                        <h4 className="font-medium">{apiKey.nombre}</h4>
                        <Badge className="bg-green-100 text-green-800">Activa</Badge>
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono border">
                          {apiKey.key_preview || '********'}
                        </code>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => copiarApiKey(apiKey.key || '')}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {apiKey.permisos.map(p => (
                          <span key={p} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => regenerarApiKey(apiKey.id)} title="Regenerar Key">
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => eliminarApiKey(apiKey.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab Backup GCS */}
        <TabsContent value="backup" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Backup del Sistema</h3>
              <p className="text-sm text-muted-foreground">Genera un .tar.gz con MongoDB + código fuente y lo sube a GCS. Se ejecuta automáticamente cada día a las 02:00.</p>
            </div>
            <Button size="sm" variant="outline" onClick={cargarHistorialBackup} disabled={backupLoading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${backupLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {/* Manual trigger */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-gray-600 mb-3">El backup incluye: <b>dump completo de MongoDB</b> (todas las colecciones) + <b>código fuente</b> del backend y frontend.</p>
              <Button onClick={ejecutarBackup} disabled={backupRunning}>
                {backupRunning
                  ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Ejecutando backup...</>
                  : <><CloudUpload className="h-4 w-4 mr-2" />Ejecutar Backup Ahora</>}
              </Button>
            </CardContent>
          </Card>

          {/* History */}
          {backupLoading && !backupHistorial.length ? (
            <div className="flex justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : backupHistorial.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No hay backups registrados todavía.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {backupHistorial.map((run, i) => (
                <Card key={i} className={`border-l-4 ${run.success ? 'border-l-green-500' : 'border-l-red-500'}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {run.success
                          ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                          : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                        <div>
                          <span className="text-sm font-medium font-mono">{run.label || run.date}</span>
                          {run.error_msg && <p className="text-xs text-red-500 mt-0.5">{run.error_msg}</p>}
                          {run.gcs_path && <p className="text-xs text-gray-400 mt-0.5">{run.gcs_path}</p>}
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500 space-y-0.5">
                        <div className="flex items-center gap-1 justify-end">
                          <Database className="h-3 w-3" />
                          <span>{run.size_mb ?? '?'} MB</span>
                        </div>
                        <div className="flex items-center gap-1 justify-end">
                          <Clock className="h-3 w-3" />
                          <span>{run.duration_s ?? 0}s</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Nueva Integración */}
      <SmartModal isOpen={mostrarFormIntegracion} onClose={() => setMostrarFormIntegracion(false)} title="Configurar Integración">
        <div className="space-y-4 py-4">
          <div>
            <Label>Nombre de la Integración</Label>
            <Input 
              value={nuevaIntegracion.nombre} 
              onChange={e => setNuevaIntegracion({...nuevaIntegracion, nombre: e.target.value})} 
              placeholder="Ej: Notificaciones Slack Soporte"
            />
          </div>
          <div>
            <Label>Servicio</Label>
            <Select 
              value={nuevaIntegracion.tipo} 
              onChange={e => {
                const tipo = tiposIntegracion.find(t => t.value === e.target.value);
                setNuevaIntegracion({
                  ...nuevaIntegracion, 
                  tipo: e.target.value,
                  nombre: tipo ? tipo.label : nuevaIntegracion.nombre
                });
              }}
            >
              <option value="">Seleccionar servicio...</option>
              {tiposIntegracion.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </div>
          
          {renderCamposConfiguracion()}

          <div className="flex items-center space-x-2 pt-2">
            <Switch 
              checked={nuevaIntegracion.activa} 
              onCheckedChange={checked => setNuevaIntegracion({...nuevaIntegracion, activa: checked})} 
            />
            <Label>Integración Activa</Label>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setMostrarFormIntegracion(false)}>Cancelar</Button>
            <Button onClick={guardarIntegracion}>Guardar Configuración</Button>
          </div>
        </div>
      </SmartModal>

      {/* Modal Nuevo Webhook */}
      <SmartModal isOpen={mostrarFormWebhook} onClose={() => setMostrarFormWebhook(false)} title="Nuevo Webhook">
        <div className="space-y-4 py-4">
          <div>
            <Label>Nombre Identificativo</Label>
            <Input 
              value={nuevoWebhook.nombre} 
              onChange={e => setNuevoWebhook({...nuevoWebhook, nombre: e.target.value})} 
              placeholder="Ej: Sincronización CRM"
            />
          </div>
          <div>
            <Label>URL de Destino (Endpoint)</Label>
            <Input 
              value={nuevoWebhook.url} 
              onChange={e => setNuevoWebhook({...nuevoWebhook, url: e.target.value})} 
              placeholder="https://api.misistema.com/webhook/elements"
            />
          </div>
          <div>
            <Label className="mb-2 block">Eventos a suscribir</Label>
            <div className="grid grid-cols-2 gap-2 border p-3 rounded-md max-h-48 overflow-y-auto bg-gray-50">
              {eventosWebhook.map(evento => (
                <div key={evento} className="flex items-center space-x-2 p-1 hover:bg-white rounded">
                  <input 
                    type="checkbox" 
                    id={`evt-${evento}`}
                    checked={nuevoWebhook.eventos.includes(evento)}
                    onChange={() => toggleEventoWebhook(evento)}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                  />
                  <label htmlFor={`evt-${evento}`} className="text-sm cursor-pointer select-none flex-1">{evento}</label>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Selecciona los eventos que dispararán este webhook.</p>
          </div>
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMostrarFormWebhook(false)}>Cancelar</Button>
            <Button onClick={guardarWebhook}>Crear Webhook</Button>
          </div>
        </div>
      </SmartModal>

      {/* Modal Nueva API Key */}
      <SmartModal isOpen={mostrarFormApiKey} onClose={() => setMostrarFormApiKey(false)} title="Generar API Key">
        <div className="space-y-4 py-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 mb-2">
            <p className="font-medium flex items-center"><Key className="h-4 w-4 mr-1"/> Importante</p>
            <p>Esta llave permitirá acceso programático a tu sistema. Compártela solo con desarrolladores de confianza.</p>
          </div>
          <div>
            <Label>Nombre / Aplicación</Label>
            <Input 
              value={nuevaApiKey.nombre} 
              onChange={e => setNuevaApiKey({...nuevaApiKey, nombre: e.target.value})} 
              placeholder="Ej: App Móvil Técnicos v2"
            />
          </div>
          <div>
            <Label className="mb-2 block">Permisos de Acceso</Label>
            <div className="grid grid-cols-2 gap-2 border p-3 rounded-md max-h-48 overflow-y-auto bg-gray-50">
              {permisosApi.map(permiso => (
                <div key={permiso} className="flex items-center space-x-2 p-1 hover:bg-white rounded">
                  <input 
                    type="checkbox" 
                    id={`perm-${permiso}`}
                    checked={nuevaApiKey.permisos.includes(permiso)}
                    onChange={() => togglePermisoApiKey(permiso)}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                  />
                  <label htmlFor={`perm-${permiso}`} className="text-sm cursor-pointer select-none flex-1">{permiso}</label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMostrarFormApiKey(false)}>Cancelar</Button>
            <Button onClick={guardarApiKey}>Generar Key Segura</Button>
          </div>
        </div>
      </SmartModal>
    </div>
  );
};

export default Integraciones;
